import React, { useEffect, useRef, useState } from 'react'
import { option1, showAspectRatioData } from 'config/static-data/editor-modal-data'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@components/ui/dropdown-menu'
import PromptSelection from './PromptSelection'
import { copyToClipboard } from 'utils/mediaUtils'
import { useRouter } from 'next/router'
import { aiBtnList, defaultSelectedModel } from 'utils/constants'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Link2, MessageCircle, Minus, Plus, Sparkles, Upload, Wand2 } from 'lucide-react'

const EditorModalHeaderDesktop = ({
  selectedParameters,
  setSelectedParameters,
  handleSelectedOptionChange,
  setAspectRatio,
  aspectRatio,
  setImagesToGenerate,
  imagesToGenerate,
  handleImageGeneration,
  setImagePrompt,
  imagePrompt,
  setActiveMedia,
  selectedAIModel,
  t = { t },
  constructModalUrl = () => {},
}) => {
  const [expandSearchInput, setExpandSearchInput] = useState(false)
  const [selectedAiModel, setSelectedAiModel] = useState(selectedAIModel || defaultSelectedModel)
  const { textToImagePrompts } = useSelector((state) => state.prompt)
  const dispatch = useDispatch()
  const router = useRouter()
  const isMobile = useMediaQuery({ maxWidth: 600 })
  const inputFileRef = useRef(null)
  const promptInputRef = useRef(null)

  const handleEnterKey = (e) => {
    e.keyCode === 13 && handleImageGeneration(selectedAiModel)
  }

  // Prevent text selection when input is focused
  const handleInputFocus = (e) => {
    // Ensure text is not selected by setting cursor at the end
    setTimeout(() => {
      if (e.target) {
        const length = e.target.value.length
        e.target.setSelectionRange(length, length)
      }
    }, 0)
  }

  // Prevent auto-selection when component mounts
  useEffect(() => {
    if (promptInputRef.current) {
      const input = promptInputRef.current
      setTimeout(() => {
        const length = input.value.length
        input.setSelectionRange(length, length)
      }, 0)
    }
  }, [])

  const handleSelectedParaRemove = (key) => {
    selectedParameters?.[key] &&
      setSelectedParameters((prev) => {
        const newParameters = { ...prev }
        delete newParameters[key]
        return newParameters
      })
  }

  const handleImageClick = () => {
    inputFileRef.current?.click()
  }

  const handleFileChange = (event) => {
    const files = event.target.files
    if (files && files.length > 0) {
      const file = files[0]
      const reader = new FileReader()
      reader.onload = (e) => {
        if (e.target) {
          setActiveMedia((prev) => ({ ...prev, img: e.target.result }))
        }
      }
      reader.readAsDataURL(file)
    }
  }

  useEffect(() => {
    if (router.query.model) {
      const model = aiBtnList.find((model) => model?.value == router?.query?.model)
      if (model) {
        setSelectedAiModel(model)
      }
    }
  }, [router.query.model])

  return (
    <div className="flex relative z-10 flex-col justify-center  space-y-4 w-full">
      <div className="flex gap-4 justify-between w-full">
        <div className="flex-1 mt-2  space-y-4 rounded-lg">
          <div className="relative mx-1">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10">
              <MessageCircle className="w-4 h-4 text-gray-400 animate-messageFloat transition-all duration-300" />
            </div>
            <Input
              type="text"
              placeholder="Enter the prompt..."
              className="py-6 pr-24 pl-10 rounded-lg transition-all border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={imagePrompt}
              onChange={(e) => setImagePrompt(e.target.value)}
              onKeyDown={handleEnterKey}
              onFocus={handleInputFocus}
              ref={promptInputRef}
            />

            <div className="flex absolute right-2  top-1/2 gap-1 items-center -translate-y-1/2 bg-white">
              <input
                ref={inputFileRef}
                type="file"
                onChange={handleFileChange}
                multiple={true}
                accept={'image/*'}
                className="hidden"
              />

              <Button
                variant="ghost"
                size="icon"
                onClick={handleImageClick}
                className="rounded-full transition-colors hover:bg-primary/10"
              >
                <Upload className="w-4 h-4 text-gray-400" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setExpandSearchInput(!expandSearchInput)}
                disabled={!textToImagePrompts}
                className="rounded-full transition-colors hover:bg-primary/10"
              >
                {expandSearchInput ? (
                  <Minus className="w-4 h-4 text-gray-400" />
                ) : (
                  <Plus className="w-4 h-4 text-gray-400" />
                )}
              </Button>

              <Button
                onClick={() => handleImageGeneration(selectedAiModel)}
                variant="default"
                size="icon"
                className="ml-1 rounded-full bg-primary hover:bg-primary/90"
              >
                <Sparkles className="w-4 h-4 text-primary-foreground" />
              </Button>
            </div>
          </div>
          <div className={`flex gap-3 text-[18px]`}>
            {Object.keys(aiBtnList)
              .filter((key) => !aiBtnList[key].ai)
              .map((key, i) => {
                return (
                  <Button
                    key={i}
                    variant={key == selectedAiModel ? 'default' : 'outline'}
                    onClick={() => setSelectedAiModel(key)}
                    className="px-7"
                  >
                    {aiBtnList[key].label}
                  </Button>
                )
              })}
          </div>

          {expandSearchInput && (
            <>
              {/* Selected Parameters */}
              {Object.keys(selectedParameters)?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {Object.keys(selectedParameters)?.map(
                    (item, i) =>
                      selectedParameters[item] && (
                        <div key={i} className="flex gap-2 items-center px-3 py-1 bg-white rounded-full border">
                          <img
                            src={i < 10 ? `/assets/editor-modal/image${i + 1}.png` : `/assets/editor-modal/image1.png`}
                            className="w-6 h-6"
                            alt="editor"
                          />
                          <span className="text-sm font-medium truncate">{selectedParameters[item]?.title}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-0 w-6 h-6"
                            onClick={() => handleSelectedParaRemove(item)}
                          >
                            <img src="/assets/common/close.svg" alt="close" className="w-4 h-4" />
                          </Button>
                        </div>
                      )
                  )}
                </div>
              )}

              {/* Controls */}
              <div className="flex gap-4 items-center">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <span className="font-medium">Ratio</span>
                      <span>{aspectRatio}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {showAspectRatioData?.map((item, i) => (
                      <DropdownMenuItem
                        key={i}
                        onClick={() => {
                          setAspectRatio(item?.value)
                        }}
                      >
                        <span className="min-w-[30px]">{item?.value}</span>
                        <span className="ml-2">{item?.label}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <div className="flex gap-2 items-center">
                  <span className="font-medium">Images</span>
                  <div className="flex items-center rounded-md border">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setImagesToGenerate(imagesToGenerate - 1)}
                      disabled={imagesToGenerate <= 1}
                    >
                      -
                    </Button>
                    <span className="w-8 font-medium text-center">{imagesToGenerate}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setImagesToGenerate(imagesToGenerate + 1)}
                      disabled={imagesToGenerate >= 4}
                    >
                      +
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="flex gap-2 justify-between w-full">
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={() => dispatch(copyToClipboard(constructModalUrl()))}>
            Share
            <Link2 className="w-4 h-4 ml-2" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                Style
                <Wand2 className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            {/* Add your style options here */}
          </DropdownMenu>
        </div>

        <Button variant="gradient" onClick={() => handleImageGeneration(selectedAiModel)}>
          {' '}
          Generate <Sparkles className="w-4 h-4" />
        </Button>
      </div>
      <div className="hidden justify-between items-center p-4 w-full bg-white rounded-none">
        <div className="flex items-center">
          <p className="text-sm text-gray-700">Use the prompts below to customize how you want your output to look.</p>
        </div>
        <div className="flex items-center">
          <label className="inline-flex items-center cursor-pointer">
            <input type="checkbox" className="text-blue-600 rounded-none border-gray-300 shadow-sm" />
            <span className="ml-2 text-sm text-gray-700">Improve Prompt</span>
          </label>
          <label className="inline-flex items-center ml-4 cursor-pointer">
            <input type="checkbox" className="text-blue-600 rounded-none border-gray-300" />
            <span className="ml-2 text-sm text-gray-700">Advanced Settings</span>
          </label>
        </div>
      </div>
      <div className="flex justify-center items-center w-full">
        <div className="w-[90%]">
          {expandSearchInput && (
            <PromptSelection
              option1={option1}
              isMobile={isMobile}
              handleSelectedOptionChange={handleSelectedOptionChange}
              selectedParameters={selectedParameters}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default EditorModalHeaderDesktop
